import type { CanonicalExam } from '../core/model/canonical.ts';
import { rebaseSession, type SessionState } from '../core/exam-engine/state.ts';
import { read, remove, write } from './store.ts';

/**
 * One session, kept so that closing the tab is not the end of it.
 *
 * The rule that shapes this file: **the exam is not stored, only the session.** What is written is
 * ids, answers, counters and times — the same material the exported result contains. When you come
 * back, the app asks for the exam file again and checks its content hash against the one recorded
 * here.
 *
 * That is more friction than keeping a copy of the file, and it is the right trade twice over.
 * Nobody's exam content ends up sitting in browser storage they forgot about; and an exam that was
 * edited in the meantime is caught rather than silently answered against the wrong questions.
 */

export interface SavedSessionDescriptor {
  readonly examId: string;
  readonly contentHash: string;
  readonly title: string;
  readonly savedAt: string;
  readonly answered: number;
  readonly total: number;
}

export interface SavedSession {
  readonly version: 1;
  readonly descriptor: SavedSessionDescriptor;
  readonly state: SessionState;
}

export type ResumeRefusal = 'none' | 'different-exam' | 'changed-content' | 'unknown-questions';

export function saveSession(state: SessionState, descriptor: SavedSessionDescriptor): boolean {
  return write('saved-session', { version: 1, descriptor, state } satisfies SavedSession);
}

export function loadSavedSession(): SavedSession | null {
  return read<SavedSession | null>('saved-session', null, revive);
}

export function clearSavedSession(): void {
  remove('saved-session');
}

/**
 * Whether this saved session can be continued against this exam, and if not, why not.
 *
 * A reason rather than a boolean, because each of the three has a different answer for the person
 * holding the file: find the right one, accept that a corrected exam starts fresh, or report a file
 * that claims an identity it does not have.
 */
export function canResume(saved: SavedSession, exam: CanonicalExam): ResumeRefusal {
  if (saved.descriptor.examId !== exam.meta.id) {
    return 'different-exam';
  }
  if (saved.descriptor.contentHash !== exam.contentHash) {
    return 'changed-content';
  }
  // The hash already implies this. Checked anyway: it is the one assumption the engine cannot
  // survive being wrong about, since every lookup afterwards is by question id.
  const known = saved.state.blueprint.questionOrder.every((id) => exam.index.byId[id] !== undefined);
  return known ? 'none' : 'unknown-questions';
}

/**
 * The draft of the exam in progress, kept in the tab.
 *
 * A different promise from the saved session above, and the difference is the whole point. This one
 * is written continuously and without being asked, because losing an hour of work to a stray
 * refresh is the worst thing this application could do to anyone. It goes to `sessionStorage`, so
 * it survives that refresh and dies with the tab — which is what keeps "closing the tab discards
 * the session" true, rather than a sentence in a document that the code quietly contradicts.
 *
 * It holds exactly what the saved session holds: ids, answers, counters, times. Never the exam.
 */
export function saveDraft(state: SessionState, descriptor: SavedSessionDescriptor): boolean {
  return write('draft', { version: 1, descriptor, state } satisfies SavedSession, 'session');
}

export function loadDraft(): SavedSession | null {
  return read<SavedSession | null>('draft', null, revive, 'session');
}

export function clearDraft(): void {
  remove('draft', 'session');
}

/** The saved session, with its clock made safe to continue. Paused, waiting for the candidate. */
export function resumeSession(saved: SavedSession, at: number): SessionState {
  return rebaseSession(saved.state, at);
}

/** The statuses a stored session may claim. Anything else was not written by this application. */
const STATUSES = new Set(['active', 'paused', 'confirming-submit', 'completed', 'reviewing']);

/**
 * Structural check of what came back off the device.
 *
 * Not a schema — the session is an internal model, not a published format, and giving it one would
 * mean maintaining a second description of every field the engine already types. `version` is what
 * carries a future format change; this is what carries a corrupt or hand-edited present one.
 *
 * What it refuses is everything the engine would act on and find wrong: a status it has no branch
 * for, a cursor pointing outside the questions drawn, a clock whose segments are not times. Each of
 * those produces a screen that is stuck rather than an error anybody could act on, which is why
 * they are worth the lines.
 */
function revive(value: unknown): SavedSession | null {
  const root = asRecord(value);
  const descriptor = asRecord(root?.descriptor);
  const state = asRecord(root?.state);

  if (root?.version !== 1 || descriptor === null || state === null) {
    return null;
  }
  if (
    typeof descriptor.examId !== 'string' ||
    typeof descriptor.contentHash !== 'string' ||
    typeof descriptor.title !== 'string'
  ) {
    return null;
  }

  const blueprint = asRecord(state.blueprint);
  const clock = asRecord(state.clock);
  const entries = asRecord(state.entries);
  const order = blueprint?.questionOrder;

  if (
    !Array.isArray(order) ||
    order.length === 0 ||
    !order.every((id) => typeof id === 'string') ||
    entries === null ||
    clock === null ||
    !Array.isArray(clock.segments) ||
    !clock.segments.every(isSegment) ||
    typeof state.status !== 'string' ||
    !STATUSES.has(state.status)
  ) {
    return null;
  }
  // The cursor indexes the questions drawn. Out of range leaves the session on a question that does
  // not exist, which the interface can only render as "no questions match".
  if (
    typeof state.cursor !== 'number' ||
    !Number.isInteger(state.cursor) ||
    state.cursor < 0 ||
    state.cursor >= order.length
  ) {
    return null;
  }
  // Every drawn question must have an entry: the engine reads one for the question on screen and
  // has no meaningful behaviour if it is missing.
  if (!order.every((id: string) => asRecord(entries[id]) !== null)) {
    return null;
  }

  return {
    version: 1,
    descriptor: descriptor as unknown as SavedSessionDescriptor,
    state: state as unknown as SessionState,
  };
}

/** A recorded stretch of time: a start, and an end that is either a time or still open. */
function isSegment(value: unknown): boolean {
  const segment = asRecord(value);
  if (segment === null || typeof segment.startedAt !== 'number' || !Number.isFinite(segment.startedAt)) {
    return false;
  }
  return (
    segment.endedAt === null ||
    (typeof segment.endedAt === 'number' && Number.isFinite(segment.endedAt))
  );
}

/** A view of an unknown value as a plain object, or null if it is not one. */
function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
